/**
 * Navigation: grille de marchabilite et A* a 8 voisins.
 *
 * Aucune dependance a three.js ni au DOM: ce module tourne aussi sur le serveur.
 * La grille fait NAV_X x NAV_Z cases de NAV_CELL metres, sur toute la CARTE
 * (les deux iles et la mer qui les separe). L'eau etant bloquee, l'A* ne trace
 * jamais d'itineraire d'une ile a l'autre: on y va a la nage, en barque ou en
 * avion, et c'est le joueur qui decide.
 *
 *   const grille = new GrilleNav(terrain)
 *   grille.bloquerEmprise(bat.pos, def.taille.l, def.taille.p, bat.rot)
 *   const chemin = trouverChemin(grille, joueur, cible)
 *   const pas = suivreChemin(joueur, chemin, vitesse * dt)
 */
import {
  ARRIVE_EPSILON,
  MAX_WALK_SLOPE,
  NAV_CELL,
  NAV_X,
  NAV_Z,
  PLAYER_RADIUS,
  CARTE_X,
  CARTE_Z,
} from '../constants.ts'
import { clamp, dist } from './alea.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'

const NB_CASES = NAV_X * NAV_Z
const RACINE2 = Math.SQRT2

/** Vrai si un point du monde est infranchissable (bord, eau, falaise). */
function pointBloque(terrain: ITerrain, x: number, z: number): boolean {
  // Une marge d'un rayon de joueur au bord du monde: on ne sort jamais.
  if (x < PLAYER_RADIUS || z < PLAYER_RADIUS) return true
  if (x > CARTE_X - PLAYER_RADIUS || z > CARTE_Z - PLAYER_RADIUS) return true
  if (terrain.estEau(x, z)) return true
  return terrain.pente(x, z) > MAX_WALK_SLOPE
}

export class GrilleNav {
  /** Nombre de cases sur chaque axe. */
  readonly taille = NAV_X
  readonly tailleZ = NAV_Z
  /** Cote d'une case en metres. */
  readonly pas = NAV_CELL

  /** 1 = terrain marchable. Fixe apres construction. */
  private readonly sol: Uint8Array
  /** Nombre d'emprises de batiment couvrant la case (0 = degage). */
  private readonly batis: Uint8Array

  constructor(terrain: ITerrain) {
    this.sol = new Uint8Array(NB_CASES)
    this.batis = new Uint8Array(NB_CASES)

    // Les coins sont partages par quatre cases: on les echantillonne une fois.
    const c = NAV_X + 1
    const coins = new Uint8Array(c * (NAV_Z + 1))
    for (let iz = 0; iz <= NAV_Z; iz++) {
      const z = iz * NAV_CELL
      const base = iz * c
      for (let ix = 0; ix < c; ix++) {
        coins[base + ix] = pointBloque(terrain, ix * NAV_CELL, z) ? 1 : 0
      }
    }

    for (let cz = 0; cz < NAV_Z; cz++) {
      const hautGauche = cz * c
      const basGauche = (cz + 1) * c
      for (let cx = 0; cx < NAV_X; cx++) {
        const bordBloque =
          coins[hautGauche + cx] === 1 ||
          coins[hautGauche + cx + 1] === 1 ||
          coins[basGauche + cx] === 1 ||
          coins[basGauche + cx + 1] === 1
        if (bordBloque) continue
        if (pointBloque(terrain, (cx + 0.5) * NAV_CELL, (cz + 0.5) * NAV_CELL)) continue
        this.sol[cz * NAV_X + cx] = 1
      }
    }
  }

  // ---------------------------------------------------------- conversions

  /** Case contenant un point du monde. Peut sortir de la grille. */
  caseDe(x: number, z: number): { cx: number; cz: number } {
    return { cx: Math.floor(x / NAV_CELL), cz: Math.floor(z / NAV_CELL) }
  }

  /** Centre de la case, en metres. */
  monde(cx: number, cz: number): Vec2 {
    return { x: (cx + 0.5) * NAV_CELL, z: (cz + 0.5) * NAV_CELL }
  }

  // ---------------------------------------------------------- consultation

  estLibre(cx: number, cz: number): boolean {
    if (cx < 0 || cz < 0 || cx >= NAV_X || cz >= NAV_Z) return false
    const i = cz * NAV_X + cx
    return this.sol[i] === 1 && this.batis[i] === 0
  }

  estLibreMonde(x: number, z: number): boolean {
    if (x < 0 || z < 0 || x >= CARTE_X || z >= CARTE_Z) return false
    return this.estLibre(Math.floor(x / NAV_CELL), Math.floor(z / NAV_CELL))
  }

  /**
   * Vrai si une EMPRISE de construction couvre ce point.
   *
   * A distinguer de `estLibreMonde`, qui melange deux choses de nature
   * differente: le terrain, dont la grille n'est qu'une approximation prudente
   * par cases de 2 m, et les emprises, qui sont exactes. Le deplacement du
   * bonhomme a besoin de faire la difference: il peut longer une falaise de plus
   * pres que l'A* ne l'y conduirait, mais il ne traverse jamais une piscine.
   */
  empriseCouvre(x: number, z: number): boolean {
    if (x < 0 || z < 0 || x >= CARTE_X || z >= CARTE_Z) return false
    const cx = Math.floor(x / NAV_CELL)
    const cz = Math.floor(z / NAV_CELL)
    return (this.batis[cz * NAV_X + cx] ?? 0) > 0
  }

  /**
   * Case libre la plus proche d'un point, centre de case renvoye en metres.
   * Sert quand l'utilisateur tape dans l'eau ou sur une falaise.
   */
  caseLibreLaPlusProche(x: number, z: number, rayonMax = 40): Vec2 | null {
    const depart = this.caseDe(x, z)
    if (this.estLibre(depart.cx, depart.cz)) return this.monde(depart.cx, depart.cz)

    const anneauMax = Math.max(1, Math.ceil(rayonMax / NAV_CELL))
    let meilleur: Vec2 | null = null
    let meilleureDist = Infinity

    for (let r = 1; r <= anneauMax; r++) {
      let trouveDansAnneau = false
      for (let cz = depart.cz - r; cz <= depart.cz + r; cz++) {
        const bordZ = cz === depart.cz - r || cz === depart.cz + r
        for (let cx = depart.cx - r; cx <= depart.cx + r; cx++) {
          // Seul le pourtour de l'anneau est neuf.
          if (!bordZ && cx !== depart.cx - r && cx !== depart.cx + r) continue
          if (!this.estLibre(cx, cz)) continue
          const p = this.monde(cx, cz)
          const d = dist(x, z, p.x, p.z)
          if (d < meilleureDist) {
            meilleureDist = d
            meilleur = p
          }
          trouveDansAnneau = true
        }
      }
      // Un anneau de plus: une case du suivant peut etre plus proche en
      // distance euclidienne qu'une case du coin de celui-ci.
      if (trouveDansAnneau && meilleureDist <= r * NAV_CELL) break
      if (trouveDansAnneau && r >= anneauMax) break
    }
    return meilleur
  }

  // ---------------------------------------------------------- emprises

  /** Marque l'emprise d'un batiment (plus une marge de PLAYER_RADIUS). */
  bloquerEmprise(centre: Vec2, l: number, p: number, rot: number): void {
    this.marquer(centre, l, p, rot, 1)
  }

  libererEmprise(centre: Vec2, l: number, p: number, rot: number): void {
    this.marquer(centre, l, p, rot, -1)
  }

  private marquer(centre: Vec2, l: number, p: number, rot: number, delta: number): void {
    // rot est un quart de tour: 1 et 3 echangent largeur et profondeur.
    const droit = (Math.abs(Math.round(rot)) & 1) === 0
    const demiX = (droit ? l : p) * 0.5 + PLAYER_RADIUS
    const demiZ = (droit ? p : l) * 0.5 + PLAYER_RADIUS

    const cx0 = Math.max(0, Math.floor((centre.x - demiX) / NAV_CELL))
    const cx1 = Math.min(NAV_X - 1, Math.ceil((centre.x + demiX) / NAV_CELL) - 1)
    const cz0 = Math.max(0, Math.floor((centre.z - demiZ) / NAV_CELL))
    const cz1 = Math.min(NAV_Z - 1, Math.ceil((centre.z + demiZ) / NAV_CELL) - 1)

    for (let cz = cz0; cz <= cz1; cz++) {
      const base = cz * NAV_X
      for (let cx = cx0; cx <= cx1; cx++) {
        const i = base + cx
        const v = this.batis[i] ?? 0
        if (delta > 0) {
          if (v < 255) this.batis[i] = v + 1
        } else if (v > 0) {
          this.batis[i] = v - 1
        }
      }
    }
  }
}

// ------------------------------------------------------- vue a la taille

/**
 * LA MEME GRILLE, VUE PAR QUELQUE CHOSE DE PLUS LARGE QU'UN PIETON.
 *
 * Une case n'y est libre que si toutes les cases a `rayon` cases d'elle le sont
 * dans la grille de base. C'est l'itineraire d'une VOITURE: elle suivait celui
 * du pieton, trace pour un point, et son emprise de 2 m accrochait la premiere
 * pente ou le premier mur au bord du couloir — « La voiture est bloquée » en
 * pleine foret, quand un detour existait (13 octobre).
 *
 * Rien n'est recopie: la vue herite de la grille par son prototype et ne
 * redefinit que `estLibre`. Toutes les methodes de la grille — l'A*, la case
 * libre la plus proche, la ligne de vue, `bloquerEmprise` — passent donc par
 * la vue dilatee, et les emprises qu'on pose ou retire sont celles de la base.
 */
export function vueDilatee(base: GrilleNav, rayon: number): GrilleNav {
  const vue = Object.create(base) as GrilleNav
  const r = Math.max(0, Math.round(rayon))
  vue.estLibre = (cx: number, cz: number): boolean => {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (!base.estLibre(cx + dx, cz + dz)) return false
      }
    }
    return true
  }
  return vue
}

// ------------------------------------------------------------- ligne de vue

/**
 * Vrai si le segment a -> b ne traverse aucune case bloquee.
 * Parcours de type Bresenham "supercover": aux passages exacts par un coin,
 * les deux cases orthogonales doivent etre libres.
 */
export function ligneDeVue(grille: GrilleNav, a: Vec2, b: Vec2): boolean {
  let cx = Math.floor(a.x / NAV_CELL)
  let cz = Math.floor(a.z / NAV_CELL)
  const cxFin = Math.floor(b.x / NAV_CELL)
  const czFin = Math.floor(b.z / NAV_CELL)

  if (!grille.estLibre(cx, cz)) return false
  if (!grille.estLibre(cxFin, czFin)) return false

  const dx = b.x - a.x
  const dz = b.z - a.z
  const pasX = dx > 0 ? 1 : -1
  const pasZ = dz > 0 ? 1 : -1

  const dtX = dx === 0 ? Infinity : Math.abs(NAV_CELL / dx)
  const dtZ = dz === 0 ? Infinity : Math.abs(NAV_CELL / dz)

  // Distance parametrique (t dans [0,1]) jusqu'a la premiere frontiere de case.
  let tX =
    dx === 0 ? Infinity : dx > 0 ? ((cx + 1) * NAV_CELL - a.x) / dx : (cx * NAV_CELL - a.x) / dx
  let tZ =
    dz === 0 ? Infinity : dz > 0 ? ((cz + 1) * NAV_CELL - a.z) / dz : (cz * NAV_CELL - a.z) / dz

  let garde = 0
  const gardeMax = (NAV_X + NAV_Z) * 2
  while ((cx !== cxFin || cz !== czFin) && garde++ < gardeMax) {
    if (tX > 1 && tZ > 1) break
    if (Math.abs(tX - tZ) < 1e-9) {
      // Passage exact par un coin: interdire la coupe de coin.
      if (!grille.estLibre(cx + pasX, cz)) return false
      if (!grille.estLibre(cx, cz + pasZ)) return false
      cx += pasX
      cz += pasZ
      tX += dtX
      tZ += dtZ
    } else if (tX < tZ) {
      cx += pasX
      tX += dtX
    } else {
      cz += pasZ
      tZ += dtZ
    }
    if (!grille.estLibre(cx, cz)) return false
  }
  return true
}

// ------------------------------------------------------------------- A*

/**
 * Tampons de recherche partages: A* est synchrone et non reentrant, ce qui
 * evite d'allouer 1 Mo de tableaux a chaque appel.
 */
const gScore = new Float64Array(NB_CASES)
const parent = new Int32Array(NB_CASES)
const vuA = new Int32Array(NB_CASES)
const ferme = new Int32Array(NB_CASES)
let versionRecherche = 0

let tasIdx = new Int32Array(2048)
let tasF = new Float64Array(2048)
let tasTaille = 0

function tasVider(): void {
  tasTaille = 0
}

function tasPousser(idx: number, f: number): void {
  if (tasTaille === tasIdx.length) {
    const ni = new Int32Array(tasIdx.length * 2)
    ni.set(tasIdx)
    tasIdx = ni
    const nf = new Float64Array(tasF.length * 2)
    nf.set(tasF)
    tasF = nf
  }
  let i = tasTaille++
  while (i > 0) {
    const p = (i - 1) >> 1
    if ((tasF[p] as number) <= f) break
    tasIdx[i] = tasIdx[p] as number
    tasF[i] = tasF[p] as number
    i = p
  }
  tasIdx[i] = idx
  tasF[i] = f
}

function tasRetirer(): number {
  const racine = tasIdx[0] as number
  tasTaille--
  if (tasTaille > 0) {
    const idx = tasIdx[tasTaille] as number
    const f = tasF[tasTaille] as number
    let i = 0
    for (;;) {
      const g = 2 * i + 1
      if (g >= tasTaille) break
      let m = g
      const d = g + 1
      if (d < tasTaille && (tasF[d] as number) < (tasF[g] as number)) m = d
      if ((tasF[m] as number) >= f) break
      tasIdx[i] = tasIdx[m] as number
      tasF[i] = tasF[m] as number
      i = m
    }
    tasIdx[i] = idx
    tasF[i] = f
  }
  return racine
}

/** Heuristique octile, en cases. */
function octile(dx: number, dz: number): number {
  const a = dx < dz ? dx : dz
  const b = dx < dz ? dz : dx
  return b - a + RACINE2 * a
}

const VOISINS_X = [1, -1, 0, 0, 1, 1, -1, -1] as const
const VOISINS_Z = [0, 0, 1, -1, 1, -1, 1, -1] as const

/**
 * LE BUDGET D'EXPANSIONS SE MESURE SUR LA DISTANCE DEMANDEE, jamais sur la
 * taille du monde.
 *
 * Il valait 20 000, ecrit pour une ile de 500 m — 250 cases de cote. Le jour ou
 * une deuxieme ile de 700 m est apparue, une traversee en diagonale demandait
 * de contourner un canyon et un lac sur 495 cases: le budget s'epuisait, et
 * l'A* rendait alors son meilleur chemin PARTIEL, en silence. Vu du joueur:
 * « le bonhomme marche presque jusqu'au bout et s'arrete ».
 *
 * C'est quadratique parce qu'un A* qui contourne un obstacle explore une
 * SURFACE, pas une ligne: c'est le detour qui coute, pas la ligne droite. Le
 * plafond existe pour qu'un tap absurde ne fige jamais une image, et le
 * plancher garde exactement l'ancien comportement sur les distances de l'ile 1.
 */
const BUDGET_BASE = 4_000
const BUDGET_PAR_CASE = 1.6
const BUDGET_MAX = 200_000

export function budgetPour(depart: Vec2, arrivee: Vec2): number {
  const cases = Math.hypot(arrivee.x - depart.x, arrivee.z - depart.z) / NAV_CELL
  return clamp(Math.round(BUDGET_BASE + BUDGET_PAR_CASE * cases * cases), BUDGET_BASE, BUDGET_MAX)
}

/**
 * Vrai si le DERNIER chemin calcule est allé jusqu'au bout.
 *
 * Un chemin partiel n'est pas une erreur — on ne bloque jamais une image — mais
 * c'est une information, et elle n'existait nulle part: le symptome etait chez
 * le joueur, la cause dans cette fonction, et cette fonction n'en disait RIEN.
 * C'est exactement ce qui a laisse vivre le bogue de la teleportation pendant
 * des semaines.
 */
let dernierComplet = true

export function dernierCheminComplet(): boolean {
  return dernierComplet
}

/**
 * A* a 8 voisins, sans coupe de coin, chemin lisse par "string pulling".
 *
 * Si le budget de noeuds est epuise, renvoie le meilleur chemin partiel vers le
 * noeud le plus proche de l'arrivee: on ne bloque jamais une frame — et
 * `dernierCheminComplet` le dit.
 * Le premier point est la position de depart, le dernier l'arrivee (ou la case
 * libre la plus proche de l'arrivee). Renvoie [] si rien n'est atteignable.
 */
export function trouverChemin(
  grille: GrilleNav,
  depart: Vec2,
  arrivee: Vec2,
  budgetNoeuds = budgetPour(depart, arrivee),
): Vec2[] {
  dernierComplet = true
  // ------- point de depart utilisable
  let d = grille.caseDe(depart.x, depart.z)
  let premier: Vec2 = { x: depart.x, z: depart.z }
  if (!grille.estLibre(d.cx, d.cz)) {
    const p = grille.caseLibreLaPlusProche(depart.x, depart.z, 20)
    if (!p) return []
    d = grille.caseDe(p.x, p.z)
    premier = p
  }

  // ------- point d'arrivee utilisable
  let a = grille.caseDe(arrivee.x, arrivee.z)
  let dernier: Vec2 = { x: arrivee.x, z: arrivee.z }
  if (!grille.estLibre(a.cx, a.cz)) {
    const p = grille.caseLibreLaPlusProche(arrivee.x, arrivee.z)
    if (!p) return []
    a = grille.caseDe(p.x, p.z)
    dernier = p
  }

  const iDepart = d.cz * NAV_X + d.cx
  const iBut = a.cz * NAV_X + a.cx
  if (iDepart === iBut) {
    return dist(premier.x, premier.z, dernier.x, dernier.z) < 1e-6 ? [premier] : [premier, dernier]
  }

  const version = ++versionRecherche
  tasVider()

  gScore[iDepart] = 0
  parent[iDepart] = -1
  vuA[iDepart] = version
  const hDepart = octile(Math.abs(d.cx - a.cx), Math.abs(d.cz - a.cz))
  tasPousser(iDepart, hDepart)

  let meilleurNoeud = iDepart
  let meilleureH = hDepart
  let atteint = false
  let expansions = 0
  const budget = budgetNoeuds > 0 ? budgetNoeuds : 20000

  while (tasTaille > 0) {
    const courant = tasRetirer()
    if (ferme[courant] === version) continue
    ferme[courant] = version

    if (courant === iBut) {
      atteint = true
      meilleurNoeud = courant
      break
    }

    if (++expansions > budget) break

    const cx = courant % NAV_X
    const cz = (courant - cx) / NAV_X
    const g = gScore[courant] as number

    for (let k = 0; k < 8; k++) {
      const vx = VOISINS_X[k] as number
      const vz = VOISINS_Z[k] as number
      const nx = cx + vx
      const nz = cz + vz
      if (!grille.estLibre(nx, nz)) continue
      if (vx !== 0 && vz !== 0) {
        // Pas de coupe de coin: les deux cases orthogonales doivent etre libres.
        if (!grille.estLibre(cx + vx, cz)) continue
        if (!grille.estLibre(cx, cz + vz)) continue
      }
      const iv = nz * NAV_X + nx
      if (ferme[iv] === version) continue
      const cout = g + (vx !== 0 && vz !== 0 ? RACINE2 : 1)
      if (vuA[iv] === version && cout >= (gScore[iv] as number)) continue
      vuA[iv] = version
      gScore[iv] = cout
      parent[iv] = courant
      const h = octile(Math.abs(nx - a.cx), Math.abs(nz - a.cz))
      if (h < meilleureH) {
        meilleureH = h
        meilleurNoeud = iv
      }
      tasPousser(iv, cout + h)
    }
  }

  dernierComplet = atteint
  const fin = atteint ? iBut : meilleurNoeud
  if (fin === iDepart) return atteint ? [premier, dernier] : [premier]

  // ------- reconstruction
  const cases: number[] = []
  for (let i = fin; i !== -1; i = parent[i] as number) {
    cases.push(i)
    if (i === iDepart) break
  }
  cases.reverse()

  const brut: Vec2[] = [premier]
  for (let k = 1; k < cases.length - 1; k++) {
    const i = cases[k] as number
    const cx = i % NAV_X
    brut.push(grille.monde(cx, (i - cx) / NAV_X))
  }
  if (atteint) {
    brut.push(dernier)
  } else {
    const cx = fin % NAV_X
    brut.push(grille.monde(cx, (fin - cx) / NAV_X))
  }

  return lisser(grille, brut)
}

/**
 * "String pulling": on garde un point seulement quand la ligne de vue depuis le
 * dernier point conserve est coupee. Un couloir degage ressort en 2 points.
 */
function lisser(grille: GrilleNav, points: Vec2[]): Vec2[] {
  const n = points.length
  if (n <= 2) return points
  const sortie: Vec2[] = [points[0] as Vec2]
  let ancre = 0
  let j = 1
  while (j < n - 1) {
    if (ligneDeVue(grille, points[ancre] as Vec2, points[j + 1] as Vec2)) {
      j++
    } else {
      sortie.push(points[j] as Vec2)
      ancre = j
      j++
    }
  }
  sortie.push(points[n - 1] as Vec2)
  return sortie
}

// ------------------------------------------------------------- suivi

export interface PasChemin {
  pos: Vec2
  restant: Vec2[]
  direction: Vec2
}

/**
 * Avance de `distance` metres le long du chemin. Fonction pure: ni `pos` ni
 * `chemin` ne sont modifies. `direction` est normalisee (0,0 si immobile).
 */
export function suivreChemin(pos: Vec2, chemin: Vec2[], distance: number): PasChemin {
  let px = pos.x
  let pz = pos.z
  let i = 0
  let reste = distance > 0 ? distance : 0
  let dirX = 0
  let dirZ = 0

  while (i < chemin.length) {
    const cible = chemin[i] as Vec2
    const d = dist(px, pz, cible.x, cible.z)
    if (d <= ARRIVE_EPSILON) {
      i++
      continue
    }
    if (reste <= 0) break
    const ux = (cible.x - px) / d
    const uz = (cible.z - pz) / d
    dirX = ux
    dirZ = uz
    if (d <= reste) {
      px = cible.x
      pz = cible.z
      reste -= d
      i++
    } else {
      px += ux * reste
      pz += uz * reste
      reste = 0
    }
  }

  return {
    pos: { x: px, z: pz },
    restant: i === 0 ? chemin.slice() : chemin.slice(i),
    direction: { x: dirX, z: dirZ },
  }
}
